import {getIoTCreds} from './aws-config'
import AWSIoTData from 'aws-iot-device-sdk'
import store from '../../store';
import AppError from '../errors/app-errors';
import AppLog from '@/services/errors/app-logs'
import { isWidgetDefaultOpened, hasProActiveGreetingsTriggered, captureIoTLogs, isUpdatedJustNow, hasProActiveGreetingsConfigured, showCWBasedOnScreenSize, isWindowMaximized, getSupportIconWidthAndHeight, configureSoundInNotificationService, handleMissedMessages  } from '../utils';
import { v4 as uuidV4 } from 'uuid'
import Storage from '@/services/storageLib'
import { IoTBotMessagesSync, IoTUserMessagesSync, IoTEventMapperSync } from './utils'
import { emitEventToParentWindow } from '@/services/utils'
import { eventType, chatWidgetScreenSizes, cookieConstant } from "../constants"
import { getCookie } from '@/services/cookie'

const mqtt = ( function() {
  let mqttClient = '';
  let previousTopic = ''
  let reconnect = {
    status: false,
    reconnectionRemaining: 0
  }
  let IoTCreds = null

  const initIOTCoreMQTTConnection = async function(conversationId = '') {
    captureIoTLogs('Init MQTT connection')
    IoTCreds = await getIoTCreds()
    if(mqttClient) return;
    mqttClient = AWSIoTData.device({
      protocol: 'wss',
      clientId: conversationId+uuidV4(), // uniqueId added for persistent connection per tab in browser
      debug: false,
      maximumReconnectTimeMs: 18000,
      qos: 1,
      host: IoTCreds.IoTHostEndPoint,
      region: IoTCreds.region,
      accessKeyId: IoTCreds.accessKeyId,
      secretKey: IoTCreds.secretAccessKey,
      sessionToken: IoTCreds.SessionToken
    })

    mqttClient.on('connect', function() {
      captureIoTLogs('MQTT connection success!')
      store.dispatch('updateMqttConnectionStatus', true)
      subscribe(conversationId);
      // NET-13259-Patch: Show CW until IoT connection is made
      if (!Storage.getSessionStorageData('userInteracted') && !isWidgetDefaultOpened()) {
        // Two cases the code will enter this condition when commection is made for 1st time else the updated script is executed
        // case 1: i.e normal case the widget is loaded in SHOW_SUPPORT_ICON_ONLY mode
        // case 2: i.e when updated show in open mode
        if(isUpdatedJustNow() || isWindowMaximized()){
          const screenSizeType = store.state.widgetStatus.isFullScreen && store.getters.isKnowledgeAiEnabled ? chatWidgetScreenSizes.SMALL_SCREEN_DEVICES : chatWidgetScreenSizes.LARGE_SCREEN_DEVICES
          emitEventToParentWindow(eventType.UPDATE_PARENT_WINDOW_STYLE, screenSizeType)
        } else {
          emitEventToParentWindow(eventType.UPDATE_PARENT_WINDOW_STYLE, getSupportIconWidthAndHeight())
        }
      }
      emitEventToParentWindow(eventType.TRIGGER_PENDING_EVENTS_ON_MQTT_CONNECT)
      if(isWidgetDefaultOpened()) {
        showCWBasedOnScreenSize()
        configureSoundInNotificationService(store.state.widgetConfig?.sound)
        if(hasProActiveGreetingsConfigured() && !hasProActiveGreetingsTriggered()) {
          store.dispatch('triggerProActiveGreetings')
        } else {
          store.dispatch('receiveHistoryOfMessages')
        }
      }
    })

    mqttClient.on('reconnect', async () => {
      try {
        if (reconnect.status || reconnect.reconnectionRemaining <= 0) return
        reconnect.status = true
        reconnect.reconnectionRemaining = reconnect.reconnectionRemaining - 1
        captureIoTLogs('Reconnecting MQTT')
        new AppLog({
          name: 'IoT Reconnect',
          message:'Reconnecting MQTT',
          logLevel: 'info',
          service: 'IoT'
        })
        IoTCreds = await getIoTCreds()
        const { accessKeyId, secretAccessKey, SessionToken } = IoTCreds
        mqttClient.updateWebSocketCredentials(accessKeyId, secretAccessKey, SessionToken)
        handleMissedMessages()
        captureIoTLogs('Successfully reconnected MQTT')
      } catch (e) {
        console.log(e)
      } finally {
        reconnect.status = false
      }
    })

    mqttClient.on('error', (error)=> {
      new AppError({
        name: "IOT Error",
        message: 'Could not create websocket connection!',
        error
      })
    })

    mqttClient.on('message', async function (_, payload) {
      try {
        captureIoTLogs('Message received !')
        const msg = JSON.parse(payload.toString())
        handleOnMessageReceive(msg)
      } catch (error) {
        console.log(`Error while handling messages from IoT`, error)
        new AppError({
          name: 'IoT message handler error',
          message: 'Error while handling messages from IoT',
          error
        })
      }
    })
  }

  const publish = function (data) {
    try {
      return new Promise ((resolve,reject)=>{
        if (!mqttClient || !mqttClient?.publish) {
          throw new Error(`MQTT Client is not valid, mqttClient: ${JSON.stringify(mqttClient)}`)
        }
        mqttClient.publish(previousTopic, JSON.stringify(data),(err)=>{
        if(err) {
          reject()
          throw new Error({
            name: 'Publishing to IoT Error',
            message: `Error while publishing to IoT, error: ${JSON.stringify(err)}`,
            error: err
          })
        } else {
          resolve()
        }
        })
      })
    } catch (error) {
      console.log(`Error while publishing to IoT`, error)
      new AppError({
        name: 'Publishing to IoT Error',
        message: `Error while publishing to IoT: ${error.message}`,
        error
      })
    }
  }


  const handleOnMessageReceive = async function (message) {
    // Empty Message
    if (Array.isArray(message) && message.length === 0) {
      return
    }
    // Bot Message
    if (!(message?.isUser || message?.isEvent)) {
      IoTBotMessagesSync(message)
      return
    }
    // User message / Event and sent from another device/tab
    if (message?.tabId !== Storage.getSessionStorageData('tabId')) {
      if (message?.isUser) {
        IoTUserMessagesSync(message)
        return
      }
      if (message?.isEvent) {
        IoTEventMapperSync[message?.event_type](message)
        return
      }
    }
  }

  const init = function(conversationId = '') {
    try {
      initIOTCoreMQTTConnection(conversationId);
    } catch (error) {
      throw new AppError({
        name: "IOT Device Error",
        message: 'Could not register device with IOT!',
        error
      })
    }
  }

  const createSubscription = function(conversationId) {
    try {
      if (!conversationId || conversationId === 'null') {
        throw new Error(`Error while subscribing to IoT, conversationId: ${conversationId}, conversationId from cookie: ${getCookie(cookieConstant.SESSION.CONVERSATION_ID)}`)
      }
      if(!mqttClient) {
        init(conversationId);
      } else {
        subscribe(conversationId);
      }
    } catch (error) {
      new AppError({
        name: 'Subscribing to IOT',
        message: error.message,
        error
      })
    }
  }

  const subscribe = function(conversationId) {
    unsubscribe();
    if (!conversationId || conversationId === 'null') {
      throw new AppError({
        name: 'Subscribe',
        message: `Error in subscribe fn, conversationId: ${conversationId}`
      })
    }
    const botRefId = store?.state?.botRefId
    const topic = `chat_widget/${botRefId}/${conversationId}`
    mqttClient.subscribe(topic);
    captureIoTLogs('MQTT subscribed')
    previousTopic = topic;
    reconnect.reconnectionRemaining = 2000
  }

  const unsubscribe = function() {
    if(previousTopic) {
      captureIoTLogs('Unsubscribed')
      mqttClient.unsubscribe(previousTopic);
    }
  }

  return {
    createSubscription,
    init,
    unsubscribe,
    publishToIoT: publish,
  };
})()


export default mqtt;
